import { Fragment, ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MapContainer, Marker, Polyline, TileLayer, useMapEvents } from 'react-leaflet';
import { DivIcon, LatLngBounds, LatLngExpression } from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useLocation, useNavigate } from 'react-router-dom';
import SectionHeader from '../../components/SectionHeader';
import Spinner from '../../components/Spinner';
import Button from '../../components/Button';
import { FitBounds } from '../../components/MapBoundsComponents';
import { getRuns } from '../../api/runs';
import { useSessionCalcData, useUpdateSession } from '../../hooks/useSessions';
import { useCreateGate, useGatesWMinRoute } from '../../hooks/useGates';
import type { Gate } from '../../types/Gate';
import toast from 'react-hot-toast';

type GateFromRunState = {
  returnTo?: string;
  sessionId: string;
  existingGatesLat?: number[];
  existingGatesLon?: number[];
  hasCallback?: boolean;
};

type PickedGate = {
  lat: number;
  lon: number;
};

type RunGpsData = {
  runId: string;
  runNumber: number;
  lats: number[];
  lons: number[];
};

function gateMarkerIcon(label: string, color: string): DivIcon {
  return new DivIcon({
    html: `
      <div style="position: relative; width: 100px; height: 100px;">
        <div style="position: absolute; left: 50%; bottom: 0; transform: translateX(-50%);">
          <div style="width: 10px; height: 10px; background-color: ${color}; border-radius: 50%;
            border: 2px solid white; box-shadow: 0 1px 4px rgba(0,0,0,0.4);"></div>
        </div>
        <div style="position: absolute; left: 50%; bottom: 8px; transform: translateX(-50%);
          width: 2px; height: 26px; background-color: ${color}; opacity: 0.7;"></div>
        <div style="position: absolute; left: 50%; bottom: 34px; transform: translateX(-50%);
          background-color: ${color}; color: white; padding: 4px 12px; font-weight: bold;
          font-size: 12px; border-radius: 4px; box-shadow: 0 2px 5px rgba(0,0,0,0.3);
          transform: translateX(-50%) rotate(-45deg); white-space: nowrap;">
          ${label}
        </div>
      </div>
    `,
    className: '',
    iconSize: [100, 100],
    iconAnchor: [50, 94],
  });
}

function existingGateIcon(label: string): DivIcon {
  return new DivIcon({
    html: `
      <div style="position: relative; width: 100px; height: 100px;">
        <div style="position: absolute; left: 50%; bottom: 0; transform: translateX(-50%);">
          <div style="width: 8px; height: 8px; background-color: #9ca3af; border-radius: 50%;
            border: 2px solid white; opacity: 0.6;"></div>
        </div>
        <div style="position: absolute; left: 50%; bottom: 6px; transform: translateX(-50%);
          width: 2px; height: 20px; background-color: #9ca3af; opacity: 0.4;"></div>
        <div style="position: absolute; left: 50%; bottom: 26px; transform: translateX(-50%);
          background-color: #9ca3af; color: white; padding: 3px 8px; font-weight: 600;
          font-size: 10px; border-radius: 4px; opacity: 0.6;
          transform: translateX(-50%) rotate(-45deg); white-space: nowrap;">
          ${label}
        </div>
      </div>
    `,
    className: '',
    iconSize: [100, 100],
    iconAnchor: [50, 94],
  });
}

const GATE_A_COLOR = '#0f766e';
const GATE_B_COLOR = '#b91c1c';
const TRACE_COLOR = '#7c3aed';

function ClickHandler({ onMapClick }: { onMapClick: (lat: number, lon: number) => void }): null {
  useMapEvents({
    click(e) {
      onMapClick(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
}

function findNearestPoint(
  clickLat: number,
  clickLon: number,
  lats: number[],
  lons: number[],
): { lat: number; lon: number; index: number } {
  let bestIndex = 0;
  let bestDist = Infinity;
  for (let i = 0; i < lats.length; i++) {
    const dLat = lats[i] - clickLat;
    const dLon = lons[i] - clickLon;
    const dist = dLat * dLat + dLon * dLon;
    if (dist < bestDist) {
      bestDist = dist;
      bestIndex = i;
    }
  }
  return { lat: lats[bestIndex], lon: lons[bestIndex], index: bestIndex };
}

export default function GateFromRunPicker(): ReactElement {
  const location = useLocation();
  const navigate = useNavigate();
  const routeState = location.state as GateFromRunState | null;
  const sessionId = routeState?.sessionId ?? '';
  const returnTo = routeState?.returnTo;
  const existingGatesLat = routeState?.existingGatesLat;
  const existingGatesLon = routeState?.existingGatesLon;

  const [allRunsGps, setAllRunsGps] = useState<RunGpsData[]>([]);
  const [isLoadingGps, setIsLoadingGps] = useState(true);
  const [loadProgress, setLoadProgress] = useState({ loaded: 0, total: 0 });
  const [gateA, setGateA] = useState<PickedGate | null>(null);
  const [gateB, setGateB] = useState<PickedGate | null>(null);
  const [fitTrigger, setFitTrigger] = useState(0);
  const [isSaving, setIsSaving] = useState(false);

  const createGate = useCreateGate();
  const updateSession = useUpdateSession(sessionId);
  const hasFetchedRef = useRef(false);
  const [isAcceptingRunGate, setIsAcceptingRunGate] = useState(false);

  const { data: session, isPending: isSessionPending } = useSessionCalcData(sessionId);
  const { data: gatesList } = useGatesWMinRoute();

  // Extract runs from session configs
  const runs = useMemo(() => {
    if (!session?.configs) return [];
    return session.configs
      .flatMap((config: any) => (config.runs || []).map((run: any) => ({ ...run })))
      .filter((run: any) => !run.deleted && run.run_status >= 4)
      .sort((a: any, b: any) => (a.run_number ?? 0) - (b.run_number ?? 0));
  }, [session]);

  // --- Run Gates Scanning ---
  // Extract unique gate coordinate sets from all runs' health data, match against gates DB
  // Gate data lives at run_health_obj.gates_run_original (the raw gate coords from the run)
  const runGateGroups = useMemo(() => {
    const allRuns = session?.configs?.flatMap((config: any) => config.runs || []) || [];
    if (allRuns.length === 0) return [];

    const coordGroups = new Map<string, { lat: number[]; lon: number[]; runNumbers: number[] }>();

    for (const run of allRuns) {
      const gateOriginal = run.run_health_obj?.sensors_present?.gates_run_original;
      if (!gateOriginal?.present) continue;

      // Build lat/lon arrays from the gate original data
      const gatesLat: number[] = gateOriginal.gates_lat ?? [];
      const gatesLon: number[] = gateOriginal.gates_lon ?? [];
      if (gatesLat.length === 0 || gatesLon.length === 0) continue;

      // Key by rounded coords (6dp ~ 0.1m) to deduplicate identical gate positions
      const key = gatesLat
        .map((v: number, i: number) => `${v.toFixed(6)},${gatesLon[i]?.toFixed(6)}`)
        .join('|');

      const existing = coordGroups.get(key);
      if (existing) {
        existing.runNumbers.push(run.run_number ?? 0);
      } else {
        coordGroups.set(key, {
          lat: gatesLat,
          lon: gatesLon,
          runNumbers: [run.run_number ?? 0],
        });
      }
    }

    const TOLERANCE = 0.0005; // ~55m tolerance for coordinate matching

    return Array.from(coordGroups.values()).map((group) => {
      const matchedGate: Gate | null = gatesList?.find((gate: Gate) => {
        if (!gate.gates_lat?.length || !gate.gates_lon?.length) return false;
        if (gate.gates_lat.length !== group.lat.length) return false;
        return gate.gates_lat.every(
          (lat: number, i: number) =>
            Math.abs(lat - group.lat[i]) < TOLERANCE &&
            Math.abs(gate.gates_lon[i] - group.lon[i]) < TOLERANCE,
        );
      }) ?? null;

      return {
        gates_lat: group.lat,
        gates_lon: group.lon,
        runCount: group.runNumbers.length,
        runNumbers: group.runNumbers.sort((a, b) => a - b),
        matchedGate,
      };
    });
  }, [session?.configs, gatesList]);

  const handleAcceptRunGate = useCallback(
    async (gateId: string) => {
      if (isAcceptingRunGate) return;
      setIsAcceptingRunGate(true);
      try {
        await updateSession.mutateAsync({ gates_id: gateId });

        if (routeState?.hasCallback && (window as any).__gateSelectCallback) {
          (window as any).__gateSelectCallback(gateId);
        }

        toast.success('Run gates accepted as session gate');

        if (returnTo) {
          navigate(returnTo);
        } else {
          navigate(-1);
        }
      } catch (error) {
        console.error('Error accepting run gate:', error);
        toast.error('Failed to accept run gate');
      } finally {
        setIsAcceptingRunGate(false);
      }
    },
    [updateSession, routeState, returnTo, navigate, isAcceptingRunGate],
  );

  // Fetch GPS data for all runs consecutively
  useEffect(() => {
    if (hasFetchedRef.current) return;
    if (runs.length === 0) {
      if (!isSessionPending) setIsLoadingGps(false);
      return;
    }
    hasFetchedRef.current = true;

    let cancelled = false;
    setLoadProgress({ loaded: 0, total: runs.length });
    setIsLoadingGps(true);

    async function fetchAllGps() {
      for (let i = 0; i < runs.length; i++) {
        if (cancelled) break;
        try {
          const runData = await getRuns(`scope=grid_data_gps&id=${runs[i].id}`);
          const fullRun = runData?.[0];
          const gridded = fullRun?.data_gridded ?? {};
          const lats: number[] = gridded.GpsLat_AG20 ?? gridded.GpsLat_AG10 ?? [];
          const lons: number[] = gridded.GpsLon_AG20 ?? gridded.GpsLon_AG10 ?? [];
          if (lats.length > 0 && !cancelled) {
            setAllRunsGps((prev) => [
              ...prev,
              { runId: runs[i].id, runNumber: runs[i].run_number ?? i + 1, lats, lons },
            ]);
            setFitTrigger((prev) => prev + 1);
          }
        } catch (err) {
          console.error(`Error loading GPS for run ${runs[i].id}:`, err);
        }
        if (!cancelled) {
          setLoadProgress({ loaded: i + 1, total: runs.length });
        }
      }
      if (!cancelled) {
        setIsLoadingGps(false);
      }
    }

    fetchAllGps();
    return () => {
      cancelled = true;
    };
  }, [runs, isSessionPending]);

  // Compute polyline coordinates per run
  const allCoordinates = useMemo(() => {
    return allRunsGps.map((runGps) => {
      const coords: LatLngExpression[] = [];
      for (let i = 0; i < runGps.lats.length; i++) {
        if (runGps.lats[i] && runGps.lons[i]) {
          coords.push([runGps.lats[i], runGps.lons[i]]);
        }
      }
      return { runId: runGps.runId, runNumber: runGps.runNumber, coords };
    });
  }, [allRunsGps]);

  // Combined bounds across all loaded runs
  const mapBounds = useMemo(() => {
    const allCoords = allCoordinates.flatMap((r) => r.coords);
    if (allCoords.length === 0) return null;
    return new LatLngBounds(allCoords);
  }, [allCoordinates]);

  const mapCenter = useMemo((): LatLngExpression => {
    const allCoords = allCoordinates.flatMap((r) => r.coords);
    if (allCoords.length === 0) return [51.5, -0.1]; // default
    return allCoords[Math.floor(allCoords.length / 2)];
  }, [allCoordinates]);

  // Gate snapping: search across all loaded runs
  const handleMapClick = useCallback(
    (lat: number, lon: number) => {
      const allLats: number[] = [];
      const allLons: number[] = [];
      for (const runGps of allRunsGps) {
        for (let i = 0; i < runGps.lats.length; i++) {
          if (runGps.lats[i] && runGps.lons[i]) {
            allLats.push(runGps.lats[i]);
            allLons.push(runGps.lons[i]);
          }
        }
      }
      if (allLats.length === 0) return;

      const nearest = findNearestPoint(lat, lon, allLats, allLons);

      if (!gateA) {
        setGateA({ lat: nearest.lat, lon: nearest.lon });
      } else if (!gateB) {
        setGateB({ lat: nearest.lat, lon: nearest.lon });
      } else {
        // Both placed — replace Gate A and clear Gate B (restart)
        setGateA({ lat: nearest.lat, lon: nearest.lon });
        setGateB(null);
      }
    },
    [gateA, gateB, allRunsGps],
  );

  const handleClearGates = useCallback(() => {
    setGateA(null);
    setGateB(null);
  }, []);

  const handleSaveGate = useCallback(async () => {
    if (!gateA || !gateB) return;
    setIsSaving(true);
    try {
      const newGate = await createGate.mutateAsync({
        id: crypto.randomUUID(),
        gates_lat: [gateA.lat, gateB.lat],
        gates_lon: [gateA.lon, gateB.lon],
        creation_mode: 0, // GatesCreation.MANUAL — protects from autocal overwrite
      });

      // Assign the new gate to the session directly
      if (sessionId && newGate?.id) {
        await updateSession.mutateAsync({ gates_id: newGate.id });
      }

      // Also fire legacy callback if available (for backward compat with RouteSelection flow)
      if (routeState?.hasCallback && (window as any).__gateSelectCallback) {
        (window as any).__gateSelectCallback(newGate.id);
      }

      toast.success('Gate created and assigned');

      if (returnTo) {
        navigate(returnTo);
      } else {
        navigate(-1);
      }
    } catch (error) {
      console.error('Error creating gate:', error);
      toast.error('Failed to create gate');
    } finally {
      setIsSaving(false);
    }
  }, [gateA, gateB, createGate, updateSession, sessionId, routeState, returnTo, navigate]);

  const hasExistingGates =
    existingGatesLat && existingGatesLon && existingGatesLat.length >= 2 && existingGatesLon.length >= 2;

  const hasAnyGps = allCoordinates.some((r) => r.coords.length > 0);

  const instructionText = !gateA
    ? 'Click on the GPS trace to place Gate A (start)'
    : !gateB
      ? 'Click on the GPS trace to place Gate B (end)'
      : 'Both gates placed. Save or click the trace to restart.';

  const isLoading = isSessionPending || (isLoadingGps && !hasAnyGps);

  return (
    <div className="cr-w-full">
      <SectionHeader
        title="Place Gates"
        isLoading={isLoadingGps && hasAnyGps}
        actions={
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="cr-flex cr-items-center cr-gap-x-1.5 cr-whitespace-nowrap cr-rounded-md cr-bg-gray-200 cr-px-3 cr-py-1.5 cr-text-sm cr-font-semibold cr-font-main cr-text-blue-900 cr-no-underline cr-transition-colors cr-duration-200 hover:cr-bg-cirrusBlue-50"
          >
            Back
          </button>
        }
      />

      {/* Instruction bar */}
      <div className="cr-mb-4 cr-flex cr-items-center cr-justify-between cr-rounded-lg cr-bg-cirrusBlue-25 cr-px-4 cr-py-3 cr-text-sm cr-font-medium cr-text-blue-900">
        <span>{isLoading ? 'Loading GPS data...' : instructionText}</span>
        {isLoadingGps && loadProgress.total > 0 && (
          <span className="cr-text-xs cr-text-gray-500">
            {loadProgress.loaded} / {loadProgress.total} runs
          </span>
        )}
      </div>

      {/* Detected Run Gates */}
      {runGateGroups.length > 0 && (
        <div className="cr-mb-4 cr-rounded-xl cr-bg-white cr-p-4 cr-shadow-sm">
          <h3 className="cr-mb-2 cr-text-sm cr-font-semibold cr-text-blue-900">
            Detected Run Gates
          </h3>
          <p className="cr-mb-3 cr-text-xs cr-text-gray-500">
            {runGateGroups.length === 1
              ? `All ${runGateGroups[0].runCount} runs share the same gates.`
              : `${runGateGroups.length} different gate sets found across runs.`}
            {' '}You can accept these as the session gate, or place new gates manually below.
          </p>
          <div className="cr-flex cr-flex-col cr-gap-2">
            {runGateGroups.map((group, idx) => {
              const gateLabel = group.matchedGate
                ? group.matchedGate.address?.trim() ||
                  group.matchedGate.description?.trim() ||
                  group.matchedGate.route_description?.trim() ||
                  group.matchedGate.id
                : null;
              const distance = group.matchedGate?.route_distance
                ? `${group.matchedGate.route_distance.toFixed(0)}m`
                : null;

              return (
                <div
                  key={idx}
                  className="cr-flex cr-items-center cr-justify-between cr-gap-3 cr-rounded-lg cr-border cr-border-gray-200 cr-bg-gray-50 cr-p-3"
                >
                  <div className="cr-flex-1">
                    {gateLabel && (
                      <p className="cr-text-sm cr-font-medium cr-text-blue-900">{gateLabel}</p>
                    )}
                    <div className="cr-flex cr-flex-wrap cr-items-center cr-gap-x-4 cr-gap-y-1 cr-text-xs cr-text-gray-600">
                      <span>
                        A: {group.gates_lat[0]?.toFixed(5)}, {group.gates_lon[0]?.toFixed(5)}
                      </span>
                      {group.gates_lat.length > 1 && (
                        <span>
                          B: {group.gates_lat[1]?.toFixed(5)}, {group.gates_lon[1]?.toFixed(5)}
                        </span>
                      )}
                      {distance && <span>Distance: {distance}</span>}
                    </div>
                    <p className="cr-mt-1 cr-text-xs cr-text-gray-500">
                      {runGateGroups.length === 1
                        ? `All ${group.runCount} runs`
                        : `Runs: ${group.runNumbers.join(', ')} (${group.runCount})`}
                      {group.matchedGate
                        ? ' — matched to existing gate'
                        : ' — no matching gate in database'}
                    </p>
                  </div>
                  {group.matchedGate && (
                    <Button
                      onClick={() => handleAcceptRunGate(group.matchedGate!.id)}
                      disabled={isAcceptingRunGate}
                    >
                      {isAcceptingRunGate ? 'Saving...' : 'Accept'}
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Map */}
      <div className="cr-relative cr-h-[500px] cr-overflow-hidden cr-rounded-xl cr-bg-white cr-shadow-sm">
        {isLoading ? (
          <div className="cr-flex cr-h-full cr-items-center cr-justify-center">
            <Spinner isFetching={true} />
            <span className="cr-ml-2 cr-text-sm cr-text-gray-500">Loading GPS data...</span>
          </div>
        ) : !hasAnyGps ? (
          <div className="cr-flex cr-h-full cr-items-center cr-justify-center cr-text-gray-500">
            No GPS data found in this session&apos;s runs.
          </div>
        ) : (
          <MapContainer
            center={mapCenter}
            zoom={15}
            scrollWheelZoom
            className="cr-h-full cr-w-full"
          >
            <FitBounds bounds={mapBounds} trigger={fitTrigger} />
            <ClickHandler onMapClick={handleMapClick} />
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />

            {/* GPS traces — one polyline per run */}
            {allCoordinates.map((run) => (
              <Polyline
                key={run.runId}
                pathOptions={{ color: TRACE_COLOR, weight: 3 }}
                positions={run.coords}
              />
            ))}

            {/* Detected run gates (shown in blue) */}
            {runGateGroups.map((group, idx) => (
              <Fragment key={`run-gate-${idx}`}>
                <Marker
                  position={[group.gates_lat[0], group.gates_lon[0]]}
                  icon={gateMarkerIcon(`Run A${runGateGroups.length > 1 ? ` (${idx + 1})` : ''}`, '#2563eb')}
                />
                {group.gates_lat.length > 1 && (
                  <Marker
                    position={[group.gates_lat[1], group.gates_lon[1]]}
                    icon={gateMarkerIcon(`Run B${runGateGroups.length > 1 ? ` (${idx + 1})` : ''}`, '#2563eb')}
                  />
                )}
              </Fragment>
            ))}

            {/* Existing gates (faded) */}
            {hasExistingGates && (
              <>
                <Marker
                  position={[existingGatesLat![0], existingGatesLon![0]]}
                  icon={existingGateIcon('Old A')}
                />
                <Marker
                  position={[existingGatesLat![1], existingGatesLon![1]]}
                  icon={existingGateIcon('Old B')}
                />
              </>
            )}

            {/* New Gate A */}
            {gateA && (
              <Marker
                position={[gateA.lat, gateA.lon]}
                icon={gateMarkerIcon('Gate A', GATE_A_COLOR)}
              />
            )}

            {/* New Gate B */}
            {gateB && (
              <Marker
                position={[gateB.lat, gateB.lon]}
                icon={gateMarkerIcon('Gate B', GATE_B_COLOR)}
              />
            )}
          </MapContainer>
        )}
      </div>

      {/* Actions panel */}
      <div className="cr-mt-4 cr-rounded-xl cr-bg-white cr-p-6 cr-shadow-sm">
        <div className="cr-flex cr-flex-wrap cr-items-center cr-justify-between cr-gap-4">
          <div className="cr-text-sm cr-text-gray-600">
            <p>
              <strong>{allRunsGps.length}</strong> run{allRunsGps.length !== 1 ? 's' : ''} loaded
              {isLoadingGps && ' (still loading...)'}
            </p>
            {gateA && (
              <p className="cr-mt-1">
                Gate A: {gateA.lat.toFixed(6)}, {gateA.lon.toFixed(6)}
              </p>
            )}
            {gateB && (
              <p>
                Gate B: {gateB.lat.toFixed(6)}, {gateB.lon.toFixed(6)}
              </p>
            )}
          </div>

          <div className="cr-flex cr-gap-2">
            <Button onClick={handleClearGates} disabled={!gateA && !gateB}>
              Clear
            </Button>
            <Button onClick={handleSaveGate} disabled={!gateA || !gateB || isSaving}>
              {isSaving ? 'Saving...' : 'Save Gate'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
